import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounce, interval, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TableStateEnum } from './table-state.enum';
import {
  SearchQuery,
} from '../../../../../../../../server/dist/angular/server/src/microservice/models/search-query.model';
import { SortOrderEnum } from '../../../../../../../../server/dist/angular/server/src/indexing/enums/sort-order.enum';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: false,
})
export class TableComponent implements OnInit, OnDestroy {
  @Output()
  refreshClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  actionClicked: EventEmitter<string> = new EventEmitter<string>();

  @Input()
  state = TableStateEnum.Loading;

  @Input()
  showHasBeenRefreshed = false;

  @Input()
  columns: { displayText: string, keyname: string, isSortable: boolean }[] = [];

  @Input()
  actions: { displayText: string, keyname: string, cssClasses: string }[] = [];

  @Input()
  searchQuery: SearchQuery = new SearchQuery();

  @Output()
  searchQueryChange = new EventEmitter<any>();

  @Input()
  totalNumberOfResults = 0;

  @Input()
  numberOfResultsReturned = 0;

  @Input()
  maximumNumberOfResultsPerPage = 100;

  @Input()
  page = 1;

  sortBy?: string;

  order?: SortOrderEnum;

  searchControl: FormControl<string | null> = new FormControl<string | null>('');

  searchControlSubscription?: Subscription;

  constructor() {
  }

  ngOnInit() {
    this.searchControlSubscription = this.searchControl.valueChanges.pipe(
      debounce(() => interval(300)),
      map(searchText => {
        this.searchQuery.query = searchText ?? undefined;

        this.searchQueryChange.emit(this.searchQuery);

        return searchText;
      }),
    ).subscribe(value => {
    });

    // We need to synchronize the sorting if it was set in the searchquery object
    const allSort = this.searchQuery.getSortedFields();

    // For now, we only support one sorting at a time in the table.
    if (allSort.length > 0) {
      this.sortBy = allSort[0].field;
      this.order = allSort[0].order;
    }
  }

  ngOnDestroy() {
    this.searchControlSubscription?.unsubscribe();
  }

  search() {
    this.searchQuery.query = this.searchControl.value ?? undefined;

    this.searchQueryChange.emit(this.searchQuery);
  }

  columnClicked(column: { displayText: string, keyname: string, isSortable: boolean }) {
    if (column.isSortable === false) {
      return;
    }

    this.toggleSort(column.keyname);
  }

  toggleSort(field: string) {
    if (this.sortBy && this.sortBy !== field) {
      this.searchQuery.clearSort(this.sortBy);
    }

    this.sortBy = field;

    this.order = this.searchQuery.toggleSort(field);

    this.searchQueryChange.emit(this.searchQuery);
  }

  totalNumberOfPages() {
    return Math.ceil(this.totalNumberOfResults / this.maximumNumberOfResultsPerPage);
  }

  pagesArray() {
    const numberOfPages = this.totalNumberOfPages();

    const pages: number[] = [];

    for (let i = 1; i <= numberOfPages; i++) {
      pages.push(i);
    }

    return pages;
  }

  pageSelected(page: number) {
    if (page === this.page || page < 1 || page > this.totalNumberOfPages()) {
      return;
    }

    this.searchQuery.page = page;

    this.searchQueryChange.emit(this.searchQuery);
  }

  setSort(field: string, order: SortOrderEnum) {
    this.sortBy = field;
    this.order = order;

    this.searchQuery.setSort(field, order);

    this.searchQueryChange.emit(this.searchQuery);
  }
}
